'use client';

import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { parseRoomInvite } from './room-invite';

type ScannerControl = { destroy: () => void; stop: () => void };

export function QrJoinScanner({
  onClose,
  onRoomCode,
}: {
  onClose: () => void;
  onRoomCode: (roomCode: string) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const scannerRef = useRef<ScannerControl | null>(null);
  const handledRef = useRef(false);
  const onRoomCodeRef = useRef(onRoomCode);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(true);
  onRoomCodeRef.current = onRoomCode;

  function acceptValue(value: string) {
    const code = parseRoomInvite(value, window.location.href);
    if (!code) {
      setError('That QR code is not a Concept room invitation.');
      return false;
    }
    if (handledRef.current) return true;
    handledRef.current = true;
    scannerRef.current?.stop();
    onRoomCodeRef.current(code);
    return true;
  }

  useEffect(() => {
    let disposed = false;

    async function startScanner() {
      const video = videoRef.current;
      if (!video) return;
      try {
        const { default: QrScanner } = await import('qr-scanner');
        if (disposed) return;
        const scanner = new QrScanner(
          video,
          (result) => acceptValue(result.data),
          {
            preferredCamera: 'environment',
            maxScansPerSecond: 8,
            highlightScanRegion: true,
            highlightCodeOutline: true,
            returnDetailedScanResult: true,
          },
        );
        scannerRef.current = scanner;
        await scanner.start();
        if (!disposed) setStarting(false);
      } catch (caught) {
        if (disposed) return;
        const denied = caught instanceof DOMException && caught.name === 'NotAllowedError';
        setError(
          denied
            ? 'Camera access was denied. Allow it in browser settings or choose a QR image.'
            : 'The camera could not start. You can choose a QR image instead.',
        );
        setStarting(false);
      }
    }

    void startScanner();
    return () => {
      disposed = true;
      scannerRef.current?.destroy();
      scannerRef.current = null;
    };
  }, []);

  async function scanImage(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setError('');
    try {
      const { default: QrScanner } = await import('qr-scanner');
      const result = await QrScanner.scanImage(file, {
        alsoTryWithoutScanRegion: true,
        returnDetailedScanResult: true,
      });
      acceptValue(result.data);
    } catch {
      setError('No readable room invitation was found in that image.');
    } finally {
      event.target.value = '';
    }
  }

  return (
    <div className="qr-scanner-backdrop">
      <section aria-label="Scan room invitation" aria-modal="true" className="qr-scanner" role="dialog">
        <header>
          <div>
            <span>Join a room</span>
            <strong>Scan QR code</strong>
          </div>
          <button aria-label="Close scanner" onClick={onClose} type="button">&times;</button>
        </header>
        <div className="qr-camera-frame">
          <video muted playsInline ref={videoRef} />
          {starting && <span>Starting camera…</span>}
        </div>
        <p>Point the camera at a Concept room invitation. The code will fill in here without opening another browser page.</p>
        {error && <p className="error">{error}</p>}
        <label className="qr-image-picker">
          Choose QR image
          <input accept="image/*" capture="environment" onChange={scanImage} type="file" />
        </label>
      </section>
    </div>
  );
}
